import { type ReactNode } from 'react';

import { Box } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import StatusBar from '@/designs-dev/device/StatusBar';
import Body from '@/karehero-app/shell/Body';
import ErrorState from '@/components/mui/ErrorState';
import CareSearchContent from '@/karehero-app/care/shortlists/CareSearchContent';
import ShortlistsEmptyState from '@/karehero-app/care/shortlists/ShortlistsEmptyState';
import { type PublishedShortlist } from '@/karehero-app/care/shortlists/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

// Shortlist home: every shortlist the care expert has published for this care
// circle. Only published lists appear here — no drafts, no partial work.
const ShortlistHome = ({
  shortlists,
  onBack,
  onOpen,
  error = false,
  onRetry,
  appBar,
}: {
  shortlists: PublishedShortlist[];
  // Omitted when this is a root tab page (Shortlists V1) rather than a pushed
  // stack page — without it the Back bar is hidden.
  onBack?: () => void;
  // App bar rendered at the top when this is a root tab page (Shortlists V1).
  appBar?: ReactNode;
  onOpen: (shortlist: PublishedShortlist) => void;
  // Load failure — forced by the handover gallery and stories.
  error?: boolean;
  onRetry?: () => void;
}) => (
  <ScreenRoot>
    <StatusBar />

    {appBar}

    {onBack && (
      <Box
        sx={{
          px: 2,
          py: 0.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Button
          variant='link'
          ariaLabel='Go back'
          iconLeft='chevron-left'
          onPress={onBack}
        >
          Back
        </Button>
      </Box>
    )}

    {error ? (
      <ErrorState
        title='We couldn’t load your shortlists'
        body='Check your connection and try again.'
        onRetry={onRetry}
      />
    ) : shortlists.length === 0 ? (
      // Sat 80px below the chrome, the same offset the waiting list uses, so the
      // two empty shortlist screens land the copy in the same place.
      <Body pt={10} px={1}>
        <ShortlistsEmptyState
          title='No shortlists yet'
          body='Your care expert is working on finding options for you. Shortlists will appear here as soon as they’re ready.'
        />
      </Body>
    ) : (
      <Body>
        <CareSearchContent shortlists={shortlists} onOpen={onOpen} />
      </Body>
    )}
  </ScreenRoot>
);

export default ShortlistHome;
